/**
 * Shared helpers used by TopicPicker (single-select) and TopicPickerMulti
 * (multi-select). Both pickers implement the same "Focused subtree" mode
 * — searching for a keyword returns only name matches, but users often
 * want to *explore inside* one of those matches (e.g. type "ISM" then
 * inspect `Rules > ISM > SMS`, whose name doesn't contain "ISM"). These
 * helpers power that flow.
 */

/**
 * Count every descendant of `topicId` reachable via `byParent`. BFS with
 * a hop cap so a corrupt parent chain can't hang the UI.
 */
export const countDescendants = (byParent, topicId) => {
  let frontier = [topicId];
  let total = 0;
  let hops = 0;
  const MAX_HOPS = 20;
  while (frontier.length && hops < MAX_HOPS) {
    const next = [];
    for (const id of frontier) {
      const children = byParent.get(id) || [];
      total += children.length;
      for (const c of children) next.push(c.id);
    }
    frontier = next;
    hops += 1;
  }
  return total;
};

/**
 * Return the full set of descendant topic ids under `rootId` (inclusive
 * of `rootId` itself). Used to scope the search inside Focused mode.
 */
export const getSubtreeIds = (byParent, rootId) => {
  const out = new Set([rootId]);
  let frontier = [rootId];
  let hops = 0;
  const MAX_HOPS = 20;
  while (frontier.length && hops < MAX_HOPS) {
    const next = [];
    for (const id of frontier) {
      for (const c of (byParent.get(id) || [])) {
        if (!out.has(c.id)) {
          out.add(c.id);
          next.push(c.id);
        }
      }
    }
    frontier = next;
    hops += 1;
  }
  return out;
};
